import React, { useEffect, useState } from "react"
import Button from "../Button/Button"
import PaymentBalance from "../PaymentBalance/PaymentBalance"
import PaymentPlanModal from "../PaymentModals/GeneratePlanModal"
import ConfirmModal from "../ConfirmModal/ConfirmModal"
import StatusContactModal from "../StatusContactModal/StatusContactModal"
import { showSnackBar } from "../Snackbar/Snackbar"
import { getDashboardBalance, getPayments, resetPlan } from "../../features/payment/paymentSlice"
import { useDispatch, useSelector } from "react-redux"
import TableList from "../../containers/Contacts/Table/Payment/TableList"
import SinglePaymentModal from "../PaymentModals/SinglePaymentModal"
import { useMyContextLoading } from "../LoadingContextProvider/LoadingContextProvider"
import { StorageGetItem, USER } from "../../lib/storage"
import { getPaymentScheduleByUser } from "../../features/creditReports/creditReports"
import { useIsSupervisor } from "../../hooks/user"
import { selectFeatureFlag } from "../../features/featureFlags/selectors"
import { buildPaymentRow } from "./buildPaymentRow"
import useLsaChargeName from "../../hooks/useLsaChargeName"
import { LuRotateCw } from "react-icons/lu"
import ConfirmDialog from "../ConfirmDialog/ConfirmDialog"
import { useUiKit } from "../UiKit/UiKitContext"
import { failureOf } from "../../utils/errorHelpers"

const PaymentTab = ({ setTriggerUpdateSidebar }) => {
  const dispatch = useDispatch()
  const currentURL = window.location.href
  const parts = currentURL.split('/')
  const userId = parts[parts?.length - 1]
  const [openResetModal, setOpenResetModal] = useState(false)
  const [paymentId, setPaymentId] = useState('')
  const [planId, setPlanId] = useState('')
  const [payments, setPayments] = useState([])
  const [reqError, setReqError] = useState('')
  const [dashboardData, setDashboardData] = useState('')
  const [isGeneratePlan, setIsGeneratePlan] = useState('')
  const [planStatus, setPlanStatus] = useState('inactive')
  const { triggerLoading } = useMyContextLoading()
  const [userRole, setUserRole] = useState('')
  const [dataPaymentsSchedule, setDataPaymentsSchedule] = useState({})
  const isSupervisor = useIsSupervisor()
  const { confirm: kitConfirm } = useUiKit()
  const lsaChargeName = useLsaChargeName(userId)

  // MDL ops, 2026-09-22: "if I reset a payment plan, the account should not automatically switch
  // to Inactive by default, as this interferes with me creating a new payment plan".
  //
  // Reset deletes the plan, so the contact then holds none, the dashboard reports no plan, and
  // this tab renders that as Inactive. Both Reset and Generate Plan were gated on the plan being
  // ACTIVE, so at that point there was nothing to activate and no way to generate a replacement.
  // Ops escaped it by generating a plan from the Credit Report tool, which starts active and
  // re-enables the buttons — a four-step detour around a gate, not a workflow.
  //
  // Generate Plan is the control that breaks the loop, so that is the gate that moves — but ONLY
  // for the state Reset actually leaves behind: NO plan at all.
  //
  // It deliberately stays disabled on an INACTIVE plan, even though ops hit that case too, and the
  // reason is that generatePaymentPlan calls Plan.create unconditionally with no existence check
  // (services/payments/plan.js). Offering it beside a plan that already exists is how a contact
  // ends up holding two, which is exactly the defect that has 1,021 contacts on more than one plan
  // and 286 on more than one ACTIVE plan, and which is why Carey Bender and Vivian Keemon are each
  // scheduled for one payment more than their balance. Unblocking the button there would trade a
  // dead end for silent over-collection. That case needs Generate Plan to REPLACE a plan rather
  // than add one, which is its own piece of work.
  //
  // Canceled stays disabled for a different reason: that client has left the programme, and
  // building them a schedule is a decision to re-enrol, not a correction. Reset keeps its own gate
  // untouched — resetting a plan that is not live is not a thing worth offering.
  const generateWithoutActive = useSelector(selectFeatureFlag('PLAN_GENERATE_WITHOUT_ACTIVE'))
  const refuseDuplicatePlan = useSelector(selectFeatureFlag('PLAN_GENERATE_REFUSE_DUPLICATE'))
  // WS 2074858. Change Plan edits the client's plan in place instead of adding a second one.
  const changeInPlace = useSelector(selectFeatureFlag('PLAN_CHANGE_IN_PLACE'))

  useEffect(() => {
    StorageGetItem(USER).then(result => {
      setUserRole(result?.role)
    })
  }, [])

  useEffect(() => {
    const handleReqError = () => {
      reqError?.length > 0 && showSnackBar("error", reqError)
      setReqError('')
    }

    handleReqError()
  }, [reqError])

  const handleOpenResetModal = () => {
    setOpenResetModal(true)
  }
  const handleCloseResetModal = () => {
    setOpenResetModal(false)
  }


  const getUsersPayments = async () => {
    triggerLoading(true)
    try {
      let result = await dispatch(getPayments(userId))
      if (result?.payload?.data && result.payload?.data?.length > 0) {
        result.payload.data.forEach((item) => {
          setPayments((prevPayments) => [...prevPayments, buildPaymentRow(item)])
        })
      } else {
        setPlanId('')
        setPayments([])
        console.log('No payment data available.')
      }
    } catch (error) {
      setReqError(error)
      console.error('An unexpected error occurred:', error)
    }
    triggerLoading(false)
  }

  const getDashboardData = async () => {
    triggerLoading(true)
    try {
      let result = await dispatch(getDashboardBalance(userId))
      setDashboardData(result?.payload?.data)
      setPlanStatus(result?.payload?.data?.plan?.status)
      setPlanId(result?.payload.data?.plan?._id)
    } catch (error) {
      setReqError(error)
      console.error('An unexpected error occurred:', error)
    }
    triggerLoading(false)
  }

  const getPaymentSchedule = async () => {
    triggerLoading(true)
    try {
      const response = await dispatch(getPaymentScheduleByUser(userId))
      if (response.payload && response.payload.data) {
        setDataPaymentsSchedule(response?.payload?.data?.map((item, index) => ({ ...item, id: index })))
      } else {
        console.error('No data received from server')
      }
    } catch (error) {
      console.error('Error:', error)
    } finally {
      triggerLoading(false)
    }
  }

  const fetchData = async () => {
    setPayments([])
    await getUsersPayments()
    await getDashboardData()
    await getPaymentSchedule()
    setTriggerUpdateSidebar(prev => !prev)
  }

  const fetchDashboardData = async () => {
    await getDashboardData()
  }
  useEffect(() => {
    fetchData()
  }, [isGeneratePlan])

  // "NO PLAN" HAS TO BE SOMETHING WE WERE TOLD, NOT SOMETHING WE FAILED TO LEARN.
  //
  // getDashboardBalance uses rejectWithValue, so a failed request RESOLVES to a rejected action
  // rather than throwing: `result.payload` is an error string, `planStatus` lands as undefined,
  // and that is indistinguishable from a contact who genuinely holds none. Opening Generate Plan
  // on that would put a SECOND plan on a contact who already has one — generatePaymentPlan calls
  // Plan.create with no existence check — and the operator would never see it happen.
  //
  // dashboardData is only set from a fulfilled response, so its presence is the proof that the
  // answer came from the server. clientPlanStatus returns null only when the contact holds zero
  // plans, so "answered, and named no plan" is exactly the state Reset leaves behind.
  const noPlanConfirmed = Boolean(dashboardData) && !planStatus
  // WS 2074858. A plan the client still holds, live or on hold (QC, paused). Change Plan edits it
  // in place, and with PLAN_GENERATE_REFUSE_DUPLICATE on it is the only way to re-plan this client.
  const holdsPlan = Boolean(dashboardData) && (planStatus === 'active' || planStatus === 'inactive')

  const handleResetPlan = async () => {
    triggerLoading(true)
    try {
      const result = await dispatch(resetPlan({ planId, userId }))
      // WS 2060104: the server refuses a reset while the plan has a payment in processing. A
      // rejected thunk resolves rather than throws, so the catch below never sees it.
      if (refuseDuplicatePlan && result?.error) showSnackBar("error", result.payload || "Failed to reset plan")
      fetchData()
    } catch (error) {
      setReqError(error)
      console.error('An unexpected error occurred:', error)
    } finally {
      handleCloseResetModal()
    }
    triggerLoading(false)
  }

  const resetPlanOrThrow = async () => {
    const result = await dispatch(resetPlan({ planId, userId }))
    if (result?.error) throw failureOf(result, "Failed to reset plan")
    fetchData()
  }

  return (
    <div className="flex flex-col w-full">
      <div className="flex justify-between w-full mb-8">
        <div className="mx-5">
          <h5 className="mb-3 text-xl-20 font-bold">Payment Plan</h5>

          <StatusContactModal planStatus={planStatus} userId={userId} fetchDashboardData={fetchDashboardData} userRole={userRole} setTriggerUpdateSidebar={setTriggerUpdateSidebar} />

        </div>
        <PaymentBalance dashboardData={dashboardData} />
      </div>
      <div className="mx-5 flex justify-between items-center border-b border-divider">
        <div className="mx-5 my-4">
          <h5 className=" text-xl-20 font-bold">Payment Schedule</h5>
          <div className="flex py-4">
            <div className="flex pr-3 gap-3 border-r-2 border-dashed border-divider">
              <span className="text-[12px]">Scheduled</span>
              <span className="text-[12px] font-bold">{dashboardData?.count?.scheduled}</span>
            </div>
            <div className="flex px-3 gap-3 border-r-2 border-dashed border-divider">
              <span className="text-[12px]">Cleared { }</span>
              <span className="text-[12px] font-bold">{dashboardData?.count?.cleared}</span>
            </div>
            <div className="flex px-3 gap-3 border-r-2 border-dashed border-divider">
              <span className="text-[12px]">Processing { }</span>
              <span className="text-[12px] font-bold">{dashboardData?.count?.processing}</span>
            </div>
            <div className="flex px-3 gap-3">
              <span className="text-[12px]">Due { }</span>
              <span className="text-[12px] font-bold">{dashboardData?.count?.dues}</span>
            </div>
          </div>
        </div>
        {(userRole === 'customer' || isSupervisor) && <div className="flex items-center mr-5">
          {/* Anything not active is off-limits, which now includes canceled. Testing for
              'inactive' by name would have left these enabled on a canceled plan — the one
              state where editing the schedule makes least sense. */}
          <Button type={"outlined"} onClick={handleOpenResetModal}
            classes={"border mr-[10px] !border-primary-main h-10 text-md"}
            width="w-max" disabled={planStatus !== 'active'}>
            Reset Plan
          </Button>
          <PaymentPlanModal userRole={userRole}
            disableModal={refuseDuplicatePlan
              // WS 2074858: the server refuses Generate beside any plan the client holds; Change Plan,
              // Reschedule and Single Payment cover what it was used for there.
              ? !noPlanConfirmed
              : generateWithoutActive
                ? !(noPlanConfirmed || planStatus === 'active')
                : planStatus !== 'active'}
            userId={userId} setIsGeneratePlan={setIsGeneratePlan}
            alreadyPaid={dashboardData?.amount?.paid} dataPaymentsSchedule={dataPaymentsSchedule} />
          {changeInPlace && (
            <div className="ml-[10px]">
              <PaymentPlanModal userRole={userRole} mode="change"
                disableModal={!holdsPlan}
                userId={userId} setIsGeneratePlan={setIsGeneratePlan}
                alreadyPaid={dashboardData?.amount?.paid} dataPaymentsSchedule={dataPaymentsSchedule} />
            </div>
          )}
          <SinglePaymentModal userId={userId} alreadyPaid={dashboardData?.amount?.paid} fetchData={fetchData} dataPaymentsSchedule={dataPaymentsSchedule} />
          {kitConfirm ? (
            <ConfirmDialog
              open={openResetModal}
              onClose={() => setOpenResetModal(false)}
              title="Reset this client's payment plan?"
              impact={['Every payment that has not cleared is deleted.', 'Cleared payments stay as they are.', 'This cannot be undone.']}
              confirmLabel="Reset plan"
              tone="danger"
              onConfirm={resetPlanOrThrow}
            />
          ) : (
            <ConfirmModal leftTopIcon={<LuRotateCw size={18} className="text-text-secondary" />} title="Are you sure to reset the plan?"
              functionButton={handleResetPlan}
              modalButtonTitle={"Reset"} classes="" open={openResetModal} setOpen={setOpenResetModal}>
              All the payments will be deleted with the exception of those that are already cleared
            </ConfirmModal>
          )}
          {/*<Button onClick={() => {*/}
          {/*}} type="outlined" classes="border !border-primary-blue-600 h-10">Quick Scheduler</Button>*/}
        </div>}
      </div>
      <TableList triggerUpdate={(e) => {
        e && fetchData()
      }} curUserId={userId} setPaymentId={setPaymentId} paymentId={paymentId} data={payments} userRole={userRole}
        lsaChargeName={lsaChargeName} />
    </div>
  )
}
export default PaymentTab
